import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { LocalizeService } from '../services/localize.service';
import { BaseDesc } from '../models/base';
import { ListTableComponent } from '../lists/list-table.component';
import { Util } from '../utils/utils.module';
import { ColFormat } from '../models/column';

@Component({
  selector: 'edx-lookup-security',
  styleUrls: ['lookup-security.component.scss'],
  templateUrl: 'lookup-security.component.html',
  standalone: false
})
export class LookupSecurityComponent  implements OnChanges, OnInit, AfterViewInit {
  @ViewChild('lookupTable') private lookupTable: ListTableComponent;
  public lookupSecurityTitle: string = '';
  public segmentItems;
  @Input() allowedList: any[] = [];
  @Input() deniedList: any[] = [];
  public hideCancel = false;
  public disabled = false;
  public okDisabled = false;
  public isReadOnly = false;
  public listDesc: BaseDesc = { lib: '', id: '', type: 'lookups' };
  public formKind = 'lookupsecurity';
  public listKind = 'PEOPLE';
  public schemaId = 'LOOKUP_SECURITY_CONFIG';
  public searchValue = '';
  public formType = 'profile';
  public lookupKey = 'USER_ID';
  public leadingColumns: number[] = [ColFormat.SELECTOR, ColFormat.OBJTYPE];
  public translationMap: any = {};
  @Input() desc;
  @Input() securityList;
  @Input() parent;

  constructor(private localizer: LocalizeService, private cdr: ChangeDetectorRef) {
    this.translationMap = {
      lookupSecurityTitle: this.localizer.getTranslation('ADMIN.LOOKUPS.LOOKUP_SECURITY'),
      allow: this.localizer.getTranslation('METADATA.FOOTER.SECURITY.ALLOW'),
      deny: this.localizer.getTranslation('METADATA.FOOTER.SECURITY.DENY'),
      allowed: this.localizer.getTranslation('ADMIN.LOOKUP_SECURITY.ALLOWED'),
      denied: this.localizer.getTranslation('ADMIN.LOOKUP_SECURITY.DENIED'),
      search: this.localizer.getTranslation('HEADER.SEARCH'),
      remove: this.localizer.getTranslation('ALT_TEXT.REMOVE'),
      clear: this.localizer.getTranslation('REFINE_SEARCH.CLEAR'),
      allowedList: this.localizer.getTranslation('ADMIN.LOOKUP_SECURITY.ALLOWED_LIST'),
      deniedList: this.localizer.getTranslation('ADMIN.LOOKUP_SECURITY.DENIED_LIST'),
    };

    this.segmentItems = [
      { display: this.localizer.getTranslation('SECURITY.USER_GROUP.USERS'), value: 'users' },
      { display: this.localizer.getTranslation('SECURITY.USER_GROUP.GROUPS'), value: 'groups' }
    ];
  }
  ngOnInit(): void {
    this.listDesc = { lib: this.desc.lib , id: Util.RestAPI.verifyLookupTable('PEOPLE_ALL', this.desc.lib), type: 'lookups' };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['allowedList'] && this.allowedList.length === 0) {
      this.addDefaultAllowedGroup();
    }
  }

  ngAfterViewInit() {
    this.listUsersOrGroups('USER_ID');
  }

  public addDefaultAllowedGroup(): void {
    this.allowedList.push({ USER_ID: 'DOCS_USERS', GROUP_NAME: 'DOCS_USERS', flag: 1, rights: 1 });
  }

  public listSelected(event) {
    const lookupKey = (event.value === 'users') ? 'USER_ID' : 'GROUP_ID';
    this.lookupTable.clearSelection();
    this.listUsersOrGroups(lookupKey);
  }
  
  public listUsersOrGroups(lookupKey) {
    this.searchValue = '';
    this.lookupKey = lookupKey;
    this.listDesc.id  = Util.RestAPI.verifyLookupTable(lookupKey === 'USER_ID' ? 'PEOPLE_ALL' : '_GROUPS_ALL', this.listDesc.lib);
    this.listKind = lookupKey === 'USER_ID' ? 'PEOPLE' : 'v_groups';
    this.desc = {};
    this.handleFilterChange(lookupKey);
    this.cdr.markForCheck();
  }

  public allowSelection() {
    this.addItemsToAllowedOrDeniedList('allow');
  }

  public denySelection() {
    this.addItemsToAllowedOrDeniedList('deny');
  }

  public transformSelectedListItem(item, allow: boolean): any {
    return {
      'USER_ID': item['USER_ID'] || item['GROUP_ID'],
      'rights': allow ? 1 : 0,
      'flag': this.lookupKey === 'GROUP_ID' ? 1 : 2
    };
  }

  public addItemsToAllowedOrDeniedList(type: string, hoverItem?: any) {
    let selectedItems;
    if (!!hoverItem) {
      selectedItems = [hoverItem];
    } else {
      selectedItems = this.lookupTable.getSelections();
    }
    for (let item of selectedItems) {
      item = this.transformSelectedListItem(item, type === 'allow');
      if (type === 'allow' && !this.isAllowedItem(item)) {
        this.allowedList.push(item);
        const deniedItem = Util.deepCopy(item);
        deniedItem['rights'] = 0;
        if (this.isDeniedItem(deniedItem)) {
          this.removeFromDenied(deniedItem);  
        }
      } else if (type === 'deny' && !this.isDeniedItem(item)) {
        this.deniedList.push(item);
        const allowedItem = Util.deepCopy(item);
        allowedItem['rights'] = 1;
        if (this.isAllowedItem(allowedItem)) {
          this.removeFromAllowed(allowedItem);
        }
      }
    }
    this.lookupTable.clearSelection();
  }

  private areTrusteesEqual(trustee1: any, trustee2: any): boolean {
    return trustee1.USER_ID === trustee2.USER_ID && trustee1.flag === trustee2.flag;
  }

  private findAllowedIndex(allowedItem: any): number {
    return this.allowedList.findIndex(item => this.areTrusteesEqual(item, allowedItem));
  }

  private findDeniedIndex(deniedItem: any): number {
    return this.deniedList.findIndex(item => this.areTrusteesEqual(item, deniedItem));
  }


  public removeFromAllowed(allowed: any) {
    this.allowedList.splice(this.findAllowedIndex(allowed),1);
  }

  public removeFromDenied(denied: any) {
    this.deniedList.splice(this.findDeniedIndex(denied),1);
  }

  public hasSelections(): boolean {
    return this.lookupTable?.getSelections().length > 0;
  }

  public itemInAccessList(item): boolean {
    return this.allowedList.includes(item) || this.deniedList.includes(item);
  }

  public isAllowedItem(item): boolean {
    return this.isTrusteeInList(this.allowedList,item);
  }

  public isTrusteeInList(list: any[], trustee: any): boolean {
    return list.some(obj => this.areTrusteesEqual(obj, trustee));
  }

  public isDeniedItem(item): boolean {
    return this.isTrusteeInList(this.deniedList,item);
  }

  public getFilterColumnsFromSchema(): any[] {
    return !!this.lookupTable ? this.lookupTable.getFilterColumnsFromSchema() : null;
  }

  public handleFilterChange(key: string): void {
    setTimeout(() => {
      if (!!this.lookupTable) {
        this.lookupTable.handleFilterChange('', key, 'includeDisabled');
      }
    }, 1);
  }
  private setDesc(lib: string): void {
    this.desc = ({ id: 'admin', type: 'admin', lib, imgPath: 'assets/images/console_settings_grey.svg' } as any);
  }

  public clearSearchWhenEmpty(event: KeyboardEvent): void {
    if (!this.searchValue && (event.key === 'Backspace' || event.key === 'Delete')) {
      this.searchClear(event, true);
    }
  }

  public getChicletIcon(item: any) {
    return item['flag'] === 2 ? 'assets/images/mime_person.svg' :'assets/images/mime_group.svg';
  }

  public enableAllow(): boolean {
    return this.hasSelections();
  }

  public enableDeny(): boolean {
    return this.hasSelections() && this.listKind !== 'v_groups';
  }

  public searchClicked(event: Event): void {
    if (!!this.searchValue.trim()) {
      const searchCol = this.lookupKey === 'GROUP_ID' ? 'GROUP_NAME' : 'FULL_NAME';
      const searchFilter = !!this.searchValue ? `${this.lookupKey}=*${this.searchValue}* or ${searchCol}=*${this.searchValue}*` : '';
      this.lookupTable.handleFilterChange(searchFilter, this.lookupKey, 'includeDisabled');
      Util.Transforms.preventDefaultAndStopPropagation(event);
    }
  }

  public searchClear(event: Event, keyClear?: boolean): void {
    if (!!this.searchValue.trim() || keyClear) {
      this.listUsersOrGroups(this.lookupKey);
    }
    this.searchValue = '';
    Util.Transforms.preventDefaultAndStopPropagation(event);
  }

  public shiftFocusToFirstRow(): void {
    const id = 'edx_tr_LOOKUP_SECURITY_CONFIG_0';
    Util.Transforms.focusOnElementById(id);
  }
  public shiftFocusToContainer(type: string): void {
    const id = `edx_${type}_list_container`;
    Util.Transforms.focusOnElementById(id);
  }

  public onKeyDown(event: KeyboardEvent, type?: string): void {
    switch (event.key) {
      case 'ArrowUp':
        void (event.ctrlKey ? this.shiftFocusToFirstRow() : this.shiftFocusToContainer('allowed'));
        break;
      case 'Space':
      case 'Enter': 
        void (type === 'allow' ? this.allowSelection() : this.denySelection());
        break;
      case 'ArrowLeft':
        this.shiftFocusToFirstRow(); 
        break; 
      case 'ArrowDown':
        this.shiftFocusToContainer('denied');
        break;
    }
  }

  public getAriaLabel(type: string): string {
    if (type === 'allowed') {
      return this.allowedList?.length > 0 ? this.translationMap.allowedList :
        this.localizer.getTranslation('ADMIN.LOOKUP_SECURITY.ACCESS_LIST_EMPTY', [this.translationMap.allow]);
    } else {
      return this.deniedList?.length > 0 ? this.translationMap.deniedList :
        this.localizer.getTranslation('ADMIN.LOOKUP_SECURITY.ACCESS_LIST_EMPTY', [this.translationMap.deny]);
    }
  }

  public getAriaLabelForChiclet(item: any): string {
    return (item['flag'] === 2 ? this.localizer.getTranslation('FORMS.LOCAL.FILTERS_MOBILE.USER') : this.localizer.getTranslation('SEARCH_SELECTOR.GROUP')) + (item['USER_ID'] || item['GROUP_ID']);
  }

  public clearList(event: Event, type: string): void {
    void(type === 'allowed' ? this.allowedList.splice(0, this.allowedList.length) : this.deniedList.splice(0,this.deniedList.length));
  }

  public commandEnabled(cmd) {
    return cmd === 'allow_mini' || (cmd === 'deny_mini' && this.listKind !== 'v_groups');
  }

  public doCommand(cmd, _ , listItem) {
    switch (cmd) {
      case 'allow_mini':
        this.addItemsToAllowedOrDeniedList('allow', listItem);
        break;
      case 'deny_mini':
        this.addItemsToAllowedOrDeniedList('deny', listItem);
        break;
    }
  }
  
}
